import { useMemo } from "react";
import { ANOMALY_LAYERS } from "../../../../enums/layers";
import useQueryState from "../../../../hooks/useQueryState";
import Chart from "../../../Chart";
import { Box } from "@mui/material";

const bgColor = ["#E1BEE766", "#C8E6C9B2", "#F57C0066", "#BBDEFBB2"];
const borderColor = ["#8E24AA", "red", "#FB8C00", "#1E88E5"];

const getBarSegments = (
  start: number,
  end: number,
): [number, number, number] => {
  if (start <= end) {
    const emptyBefore = start - 1;
    const filled = end - start + 1;
    const emptyAfter = 12 - end;
    return [emptyBefore, filled, emptyAfter];
  } else {
    const emptyAfter = 12 - start + 1;
    const emptyBefore = end;
    const filled = 12 - emptyBefore - emptyAfter;
    return [emptyBefore, filled, emptyAfter];
  }
};

const PeriodChart = ({ season }: { season: string }) => {
  const {
    queryState: { layers },
  } = useQueryState();

  const anomalyLabels = [
    "Hybrid Index",
    "Vegetation",
    "Soil Moisture",
    "Precipitation",
  ];

  const anomalyLayers = useMemo(() => {
    const base: Record<string, string[]> = {
      "Hybrid Index": ["", ""],
      Vegetation: ["", ""],
      "Soil Moisture": ["", ""],
      Precipitation: ["", ""],
    };

    // defaults from enum
    ANOMALY_LAYERS.forEach((layer) => {
      if (layer.displayName) {
        base[layer.displayName] = layer.months;
      }
    });

    // override by selected season
    layers.forEach((layer) => {
      if (layer.season === season && layer.displayName) {
        base[layer.displayName] = layer.months;
      }
    });

    return base;
  }, [layers, season]);

  const series = useMemo(() => {
    const startData: number[] = [];
    const centerData: number[] = [];
    const endData: number[] = [];

    Object.values(anomalyLayers).forEach(([start, end]) => {
      const [emptyBefore, filled, emptyAfter] = getBarSegments(
        Number(start),
        Number(end),
      );

      startData.push(emptyBefore);
      centerData.push(filled);
      endData.push(emptyAfter);
    });

    return [
      {
        name: "start",
        type: "bar",
        stack: "total",
        data: startData,
        itemStyle: {
          // borderColor: (params: any) => {
          //   const obs = Object.values(anomalyLayers)[params.dataIndex];
          //   return Number(obs?.[0]) > Number(obs?.[1])
          //     ? "transparent"
          //     : borderColor[params.dataIndex % bgColor.length];
          // },
          color: (params: any) => {
            const obs = Object.values(anomalyLayers)[params.dataIndex];
            return Number(obs?.[0]) > Number(obs?.[1])
              ? bgColor[params.dataIndex % bgColor.length]
              : "transparent";
          },
        },
      },
      {
        name: "center",
        type: "bar",
        stack: "total",
        data: centerData,
        itemStyle: {
          // borderColor: (params: any) => {
          //   const obs = Object.values(anomalyLayers)[params.dataIndex];
          //   return Number(obs?.[0]) <= Number(obs?.[1])
          //     ? borderColor[params.dataIndex % bgColor.length]
          //     : "transparent";
          // },
          color: (params: any) => {
            const obs = Object.values(anomalyLayers)[params.dataIndex];
            return Number(obs?.[0]) <= Number(obs?.[1])
              ? bgColor[params.dataIndex % bgColor.length]
              : "transparent";
          },
        },
      },
      {
        name: "end",
        type: "bar",
        stack: "total",
        data: endData,
        itemStyle: {
          // borderColor: (params: any) => {
          //   const obs = Object.values(anomalyLayers)[params.dataIndex];
          //   return Number(obs?.[0]) > Number(obs?.[1])
          //     ? "transparent"
          //     : borderColor[params.dataIndex % bgColor.length];
          // },
          color: (params: any) => {
            const obs = Object.values(anomalyLayers)[params.dataIndex];
            return Number(obs?.[0]) > Number(obs?.[1])
              ? bgColor[params.dataIndex % bgColor.length]
              : "transparent";
          },
        },
      },
    ];
  }, [anomalyLayers]);

  const option = {
    tooltip: {
      show: true,
      axisPointer: {
        show: false,
        type: "none",
      },
      formatter: (params: any) => {
        return anomalyLabels?.[params[0].dataIndex];
      },
    },
    grid: { left: 0, right: 0, top: 0, bottom: 0 },
    yAxis: { show: false, type: "category" },
    xAxis: {
      type: "value",
      min: 0,
      max: 12,
      interval: 1,
      splitLine: { lineStyle: { color: "#BBDEFB" } },
      axisPointer: {
        show: false,
      },
      axisLabel: {
        margin: 0,
        align: "left",
        show: true,
        interval: 0,
        rotate: 0,
        padding: [0, 0, 0, 2.5],
        fontSize: 10,
        formatter: (v: number) =>
          [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ][v] || "",
      },
    },
    series,
  };

  return (
    <Box bgcolor="#FAFAFA" border="1px solid #E8E8ED" borderRadius="4px">
      <Box m={0.5}>
        <Chart onLegendSelectChanged={() => {}} height={72} option={option} />
      </Box>
    </Box>
  );
};

export default PeriodChart;
